import { FC, useMemo } from 'react';
import type { FinancialHealthStats } from '../../types/financialStats';
import { BaseChartTemplate } from './BaseChartTemplate';

// Constants for styling
const LINE_STYLES = {
  CURVED: 0.4
};

interface MoneyLeftoverChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<any>;
  monthsToShow?: number;
}

// Helper function to calculate 3-month average
const calculate3MonthAverage = (data: FinancialHealthStats[]): number => {
  if (data.length < 3) return 0;
  const last3Months = data.slice(-3);
  return last3Months.reduce((sum, month) => sum + (month.money_leftover || 0), 0) / 3;
};

// Helper function to calculate savings potential
const getSavingsPotential = (currentLeftover: number, avgLeftover: number) => {
  const percentDiff = ((currentLeftover - avgLeftover) / avgLeftover) * 100;

  if (currentLeftover <= 0)
    return {
      icon: '⚠️',
      label: 'No Savings',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    };

  if (percentDiff > 10)
    return {
      icon: '💰',
      label: 'High Potential',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    };

  if (percentDiff >= 0)
    return {
      icon: '💫',
      label: 'Moderate',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    };

  return {
    icon: '📉',
    label: 'Below Average',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  };
};

// Helper function to calculate percentage of income saved
const calculateSavingsRate = (moneyLeftover: number, moneyIn: number): number => {
  if (!moneyIn) return 0;
  return (moneyLeftover / moneyIn) * 100;
};

// Helper function to calculate trend line using linear regression
const calculateTrendLine = (data: number[]): number[] => {
  if (data.length <= 2) return data;
  
  // Use linear regression to calculate trend line
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  const n = data.length;
  
  // Calculate sums for linear regression formula
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += data[i];
    sumXY += i * data[i];
    sumXX += i * i;
  }
  
  // Calculate slope and y-intercept
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Generate trend line points
  return data.map((_, i) => intercept + slope * i);
};

export const MoneyLeftoverChart: FC<MoneyLeftoverChartProps> = ({ 
  data, 
  showDebug,
  onExpandClick,
  monthsToShow = 24
}) => {
  // Format currency for Y-axis
  const formatCurrency = (value: number) => {
    if (value === 0) return '$0';
    if (value % 1000 === 0) return `$${(value/1000)}k`;
    return `$${value.toLocaleString()}`;
  };
  
  // Format tooltip values
  const formatTooltip = (value: number) => [`$${value.toLocaleString()}`, 'Money Leftover'];
  
  // Calculate maximum for Y-axis (20% above the max value, rounded to nearest $1000)
  const calculateYMax = useMemo(() => {
    if (data.length === 0) return 5000;
    
    const maxValue = Math.max(...data.map(item => item.money_leftover || 0));
    if (maxValue === 0) return 5000;
    
    // Add 20% buffer and round up to nearest $1000
    const withBuffer = maxValue * 1.2;
    return Math.ceil(withBuffer / 1000) * 1000;
  }, [data]);
  
  // Prepare chart datasets including the main line and trend line
  const getChartDatasets = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Sort data chronologically
    const sortedData = [...data].sort((a, b) => 
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    // Get the most recent months up to monthsToShow
    const visibleData = sortedData.slice(-monthsToShow);
    
    // Extract money leftover values
    const values = visibleData.map(d => d.money_leftover || 0);
    
    // Calculate trend line
    const trendLine = calculateTrendLine(values);
    
    return [
      {
        // Main data line
        label: 'Money Leftover',
        data: values,
        fill: true,
        tension: LINE_STYLES.CURVED,
        borderWidth: 2.5,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointBorderColor: 'rgba(16, 185, 129, 1)',
        pointBorderWidth: 2,
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
      },
      {
        // Trend line
        label: 'Trend',
        data: trendLine,
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        borderColor: 'rgba(107, 114, 128, 0.8)',
        backgroundColor: 'transparent',
      }
    ];
  }, [data, monthsToShow]);
  
  // Render the overlay with current data
  const renderOverlay = (visibleData: FinancialHealthStats[]) => {
    // Calculate metrics for the overlay
    const currentLeftover = visibleData[visibleData.length - 1]?.money_leftover || 0;
    const previousLeftover = visibleData[visibleData.length - 2]?.money_leftover || 0;
    const monthlyChange = currentLeftover - previousLeftover;
    const threeMonthAvg = calculate3MonthAverage(visibleData);
    const savingsPotential = getSavingsPotential(currentLeftover, threeMonthAvg);

    // Calculate savings rate
    const currentMoneyIn = visibleData[visibleData.length - 1]?.money_in || 0;
    const savingsRate = calculateSavingsRate(currentLeftover, currentMoneyIn);

    return (
      <div 
        className="absolute left-16 top-2 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-lg border border-slate-200 shadow-sm"
        style={{ zIndex: 1 }}
      >
        <div className="grid gap-4">
          {/* Current Money Leftover */}
          <div>
            <div className="text-xs text-slate-500 mb-1">Money Leftover</div>
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold text-brand-blue">
                ${currentLeftover.toLocaleString()}
              </div>
              <div
                className={`text-sm font-medium px-2 py-1 rounded-full ${savingsPotential.color} ${savingsPotential.bgColor}`}
              >
                {savingsRate.toFixed(1)}% Saved
              </div>
            </div>
          </div>

          {/* Monthly Change & Savings Potential */}
          <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
            <div>
              <div className="text-xs text-slate-500">Monthly Change</div>
              <div className="flex items-center gap-2">
                <div
                  className="text-2xl font-semibold"
                  style={{ color: monthlyChange >= 0 ? '#10B981' : '#EF4444' }}
                >
                  {monthlyChange >= 0 ? '↑' : '↓'}
                </div>
                <div className="text-sm font-medium">${Math.abs(monthlyChange).toLocaleString()}</div>
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-500">Savings Potential</div>
              <div className="flex items-center gap-2">
                <span className="text-lg">{savingsPotential.icon}</span>
                <span className={`text-sm font-medium ${savingsPotential.color}`}>
                  {savingsPotential.label}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      <BaseChartTemplate
        data={data}
        showDebug={showDebug}
        monthsToShow={monthsToShow}
        onExpandClick={onExpandClick}
        title="Money Leftover"
        dataKey="money_leftover"
        color="#10B981"
        yAxisFormatter={formatCurrency}
        tooltipFormatter={formatTooltip}
        renderOverlay={renderOverlay}
        isCurrentFinances={true}
        min={0}
        max={calculateYMax}
        pointRadius={3}
        pointBackgroundColor="rgba(255, 255, 255, 0.9)"
        pointBorderColor="rgba(16, 185, 129, 1)"
        pointBorderWidth={2}
        borderWidth={2.5}
        tension={LINE_STYLES.CURVED}
        customDatasets={getChartDatasets}
      />
    </div>
  );
};
